/**
 * Workout Session Service
 * Manages workout session lifecycle including starting, stopping, and managing session state.
 *
 * This service handles server-side operations (start/stop) and orchestrates local session management
 * using the existing session-utils functions.
 */

import { container, inject, injectable } from 'tsyringe';
import { ZodError } from 'zod';

import { WORKOUT_CONSTANTS } from '../config/constants';
import { TOKENS } from '../di/container';
import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { CrudService } from './crud.service';
import { syncGetAccountUsername } from '../store/store-sync';
import { ApiErrorClass } from '../types/api/errors';
import { sessionRefusal } from '../utils/workout/session-controller/session-refusal';
import {
  serializeSessionForStorage,
  deserializeSessionFromStorage,
  mergeSessionProgress,
} from '../utils/workout/session-utils';
import {
  safeValidateSessionState,
  validateWorkoutSession,
} from '../validation/workout.schemas';

import type { SuccessApiResponse } from '../types/api.types';
import type { ID } from '../types/common/id.types';
import type { IStorageService } from '../types/common/storage.types';
import type {
  ComparableSession,
  ComparableSessions,
  ComparableSessionsMetadata,
  SameWorkoutSession,
  SharedExerciseSession,
} from '../types/domain/comparable-sessions.types';
import type { CompletedWorkoutDetail } from '../types/domain/completed-workout.types';
import type { WorkoutComparison } from '../types/domain/workout-comparison.types';
import type {
  SessionState,
  SerializedSession,
  CompareWorkoutSessionsParams,
  PreWorkout,
  WorkoutExercise,
} from '../types/domain/workout-session.types';
import type { WorkoutSessionInput } from '../validation/workout.schemas';

/**
 * Local storage key for session data
 */
const SESSION_STORAGE_KEY = WORKOUT_CONSTANTS.SESSION_STORAGE_KEY;

/**
 * The in-progress workout is one account's, so it is stored under that
 * account's name (docs/roadmap/session-scoped-state.md, D6): the account
 * keeps its resume prompt after signing back in, and the next account never
 * sees it. With nobody signed in — tests, and nothing else — the bare key.
 */
const sessionStorageKey = (): string => {
  const username = syncGetAccountUsername();
  return username ? `${SESSION_STORAGE_KEY}:${username}` : SESSION_STORAGE_KEY;
};

/**
 * Workout Session Service
 *
 * Handles workout session management with both server-side API calls
 * and local session state persistence using existing utilities.
 */
@injectable()
export class WorkoutSessionService extends CrudService<CompletedWorkoutDetail> {
  constructor(@inject(TOKENS.STORAGE) private storageService: IStorageService) {
    super({ baseUrl: '/completed-workout' });
  }

  /**
   * Start a workout session
   *
   * @param id - Workout template ID
   * @returns Promise resolving to pre-workout data
   * @throws Error if session cannot be started
   */
  public async startSession(id: ID): Promise<PreWorkout> {
    if (!id) {
      throw new Error('Workout ID is required');
    }

    try {
      const response = await apiClient.get<SuccessApiResponse<PreWorkout>>(
        `/workout/${id}/start`
      );

      if (response.data.data) {
        return response.data.data;
      }

      throw new Error('Failed to start workout session');
    } catch (error) {
      // Preserve the coordinator's typed error (incl. any 402) before the
      // pre-flight guard checks below.
      if (error instanceof ApiErrorClass) throw error;
      if (
        error instanceof Error &&
        error.message === 'Workout ID is required'
      ) {
        throw error;
      }
      console.error(`Failed to start workout session for ${id}:`, error);
      throw new Error('Unable to start workout session. Please try again.');
    }
  }

  /**
   * Stop a workout session and submit completed data
   *
   * @param id - Session ID
   * @param data - Completed workout session data
   * @returns Promise resolving to the completed-workout summary
   * @throws Error if session cannot be stopped
   */
  public async stopSession(
    id: ID,
    data: WorkoutSessionInput
  ): Promise<CompletedWorkoutDetail> {
    if (!id) {
      throw new Error('Workout session ID is required');
    }

    if (!data) {
      throw new Error('Workout session data is required');
    }

    // Validate workout session data
    this.validateSessionData(data);

    try {
      const response = await apiClient.post<
        SuccessApiResponse<CompletedWorkoutDetail>
      >(`/workout/${id}/stop`, data);

      if (response.data.data) {
        // Clear local session data after successful submission
        await this.clearLocalSession();
        return response.data.data;
      }

      throw new Error('Failed to stop workout session');
    } catch (error) {
      if (error instanceof ApiErrorClass) {
        // The server has no session for this workout: the local copy is
        // stale and a retry can only be refused again.
        if (sessionRefusal(error)) await this.clearLocalSession();
        throw error;
      }
      // Re-throw validation errors
      if (
        error instanceof Error &&
        (error.message === 'Workout session ID is required' ||
          error.message === 'Workout session data is required')
      ) {
        throw error;
      }
      console.error(`Failed to stop workout session for ${id}:`, error);
      throw new Error('Unable to stop workout session. Please try again.');
    }
  }

  /**
   * Cancel the live workout session
   *
   * Discards the server-side session (nothing is logged) and clears the local
   * copy. Also clears local state when the server has no session for this
   * workout (either `sessionRefusal`) — an orphaned local session would
   * otherwise block starting a new one.
   *
   * @param id - Workout template ID
   * @throws Error if the session cannot be cancelled
   */
  public async cancelSession(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Workout ID is required');
    }

    try {
      await apiClient.post(`/workout/${id}/cancel`);
      await this.clearLocalSession();
    } catch (error) {
      if (error instanceof ApiErrorClass) {
        if (sessionRefusal(error)) {
          // No server session for this workout — the local copy is orphaned.
          await this.clearLocalSession();
          return;
        }
        throw error;
      }
      if (
        error instanceof Error &&
        error.message === 'Workout ID is required'
      ) {
        throw error;
      }
      console.error(`Failed to cancel workout session for ${id}:`, error);
      throw new Error('Unable to cancel workout session. Please try again.');
    }
  }

  /**
   * Get a completed session's details by ID
   *
   * @param id - Session ID
   * @returns Promise resolving to post workout
   * @throws Error if session cannot be retrieved
   */
  public async getSessionById(id: ID): Promise<CompletedWorkoutDetail> {
    if (!id) {
      throw new Error('Session ID is required');
    }

    try {
      return await this.getById(id);
    } catch (error) {
      if (error instanceof ApiErrorClass) throw error;
      if (
        error instanceof Error &&
        error.message === 'Session ID is required'
      ) {
        throw error;
      }
      console.error(`Failed to get session ${id}:`, error);
      throw new Error('Unable to retrieve session data. Please try again.');
    }
  }

  /**
   * The earlier sessions this one may be read against.
   *
   * Owner-only, and exactly the set the nomination rule admits — so a picker
   * built on this cannot offer a session `compareWorkoutSessions` will then
   * reject. The first row is the session the comparison falls back to when no
   * `session2` is given.
   *
   * Returns the whole envelope rather than just the rows. `matched_on` says
   * which of two tiers answered and therefore which of two lists the caller is
   * holding, and `limit` is the cap actually applied — copy that names the cap
   * has to read it rather than hardcode 12. Both live in `metadata`, so
   * returning `response.data.data` alone would drop the two fields the call
   * exists to deliver.
   *
   * An empty list is a STATE, not a failure: unlike `/comparison`, this
   * endpoint answers 200 with no rows when there is nothing comparable.
   *
   * @example
   * ```typescript
   * const result = await workoutSessionService.getComparableSessions('abc123');
   * if (isSharedExerciseMatch(result)) {
   *   // rows carry `shared_exercises`; narrowing is the only way to reach it
   *   result.sessions.map((s) => `${s.workout_name} — shares ${s.shared_exercises}`);
   * }
   * ```
   */
  public async getComparableSessions(
    sessionId: ID
  ): Promise<ComparableSessions> {
    if (!sessionId) {
      throw new Error('Session ID is required to list comparable sessions');
    }

    try {
      const response = await apiClient.get<
        SuccessApiResponse<ComparableSession[], ComparableSessionsMetadata>
      >(`/completed-workout/${sessionId}/comparable`);

      const sessions = response.data.data ?? [];
      const metadata = response.data.metadata;
      // `limit` is echoed so the caller can name the cap; falling back to the
      // row count keeps a truncation claim from being invented if it is ever
      // absent — better to understate the cap than to assert a wrong one.
      const limit = metadata?.limit ?? sessions.length;

      // The tiers never mix, so the match decides which row type the whole
      // list is. Casting once here is what buys every caller a narrowed union
      // instead of a nullable count on every row.
      switch (metadata?.matched_on) {
        case 'same_workout':
          return {
            matchedOn: 'same_workout',
            limit,
            sessions: sessions as SameWorkoutSession[],
          };
        case 'shared_exercises':
          return {
            matchedOn: 'shared_exercises',
            limit,
            sessions: sessions as SharedExerciseSession[],
          };
        default:
          // No match reported means no rows produced it. Anything else would
          // claim a provenance the list does not have.
          return { matchedOn: null, limit, sessions: [] };
      }
    } catch (error) {
      if (error instanceof ApiErrorClass) throw error;
      if (error instanceof Error && error.message.includes('required')) {
        throw error;
      }

      console.error('Failed to list comparable sessions:', error);
      throw new Error(
        'Unable to load the sessions this one can be compared against.'
      );
    }
  }

  /**
   * Compare workout sessions to track progress
   * Analyzes performance improvements between current and previous sessions
   *
   * @param params - Comparison parameters including session IDs
   * @returns Promise resolving to detailed comparison analysis
   *
   * @example
   * ```typescript
   * const comparison = await workoutSessionService.compareWorkoutSessions({
   *   session1: 'session-current',
   *   session2: 'session-previous' // Optional - will use latest if not provided
   * });
   * ```
   */
  public async compareWorkoutSessions(
    params: CompareWorkoutSessionsParams
  ): Promise<WorkoutComparison> {
    if (!params.session1) {
      throw new Error('Primary session ID is required for comparison');
    }

    try {
      // Build comparison endpoint URL
      const baseUrl = `/completed-workout/${params.session1}/comparison`;
      const queryParams = params.session2
        ? { compare_with: params.session2 }
        : {};

      const response = await apiClient.get<
        SuccessApiResponse<WorkoutComparison>
      >(baseUrl, { params: queryParams });

      if (response.data.data) {
        return response.data.data;
      }

      throw new Error('Comparison request failed');
    } catch (error) {
      console.error('Failed to compare workout sessions:', error);

      if (error instanceof ApiErrorClass) throw error;
      if (error instanceof Error && error.message.includes('required')) {
        throw error; // Re-throw validation errors as-is
      }

      throw new Error(
        'Unable to compare workout sessions. Please check the session IDs and try again.'
      );
    }
  }

  /**
   * Update exercise log during session
   *
   * This method updates local session state only, as sessions are stored
   * locally until completion.
   *
   * @param sessionId - Session ID
   * @param exerciseId - Exercise ID/GUID
   * @param data - Updated exercise data
   * @returns Promise resolving to updated session state
   * @throws Error if exercise cannot be updated
   */
  public async updateExerciseLog(
    sessionId: ID,
    exerciseId: ID,
    data: WorkoutExercise
  ): Promise<SessionState> {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    if (!exerciseId) {
      throw new Error('Exercise ID is required');
    }

    if (!data) {
      throw new Error('Exercise data is required');
    }

    try {
      const currentSession = await this.getLocalSession();
      if (!currentSession) {
        throw new Error('No active session found');
      }

      if (currentSession.workout.workout_id !== sessionId.toString()) {
        throw new Error('Session ID mismatch');
      }

      // Find and update the exercise in the workout
      const updatedExercises = currentSession.workout.exercises.map(
        (exercise) =>
          exercise.guid === exerciseId.toString()
            ? { ...exercise, ...data }
            : exercise
      );

      // Update completed exercises if this exercise is being marked as completed
      let updatedCompletedExercises = currentSession.completed_exercises || [];
      const isAlreadyCompleted = updatedCompletedExercises.some(
        (ex) => ex.guid === exerciseId.toString()
      );

      if (data.sets && data.sets.length > 0 && !isAlreadyCompleted) {
        updatedCompletedExercises = [...updatedCompletedExercises, data];
      }

      // Use existing utility to merge progress
      const updatedSession = mergeSessionProgress(currentSession, {
        workout: {
          ...currentSession.workout,
          exercises: updatedExercises,
        },
        completed_exercises: updatedCompletedExercises,
      });

      // Save updated session using existing utilities
      await this.saveLocalSession(updatedSession);
      return updatedSession;
    } catch (error) {
      if (error instanceof ApiErrorClass) throw error;
      // Re-throw validation errors
      if (
        error instanceof Error &&
        (error.message === 'Session ID is required' ||
          error.message === 'Exercise ID is required' ||
          error.message === 'Exercise data is required' ||
          error.message === 'No active session found' ||
          error.message === 'Session ID mismatch')
      ) {
        throw error;
      }
      console.error(
        `Failed to update exercise log for session ${sessionId}, exercise ${exerciseId}:`,
        error
      );
      throw new Error('Unable to update exercise log. Please try again.');
    }
  }

  /**
   * Save session progress to local storage
   *
   * Persists current session state locally until the session is completed
   * and submitted to the server.
   *
   * @param sessionId - Session ID
   * @param data - Session state to save
   * @returns Promise resolving to saved session state
   * @throws Error if session cannot be saved
   */
  public async saveSessionProgress(
    sessionId: ID,
    data: SessionState
  ): Promise<SessionState> {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    if (!data) {
      throw new Error('Session data is required');
    }

    try {
      // Validate session data using schema validation
      const parseResult = safeValidateSessionState(data);
      if (!parseResult.success) {
        const errorMessages = parseResult.error.issues.map((e) => e.message);
        throw new Error(`Invalid session data: ${errorMessages.join(', ')}`);
      }

      // Verify session ID matches
      if (data.workout.workout_id !== sessionId.toString()) {
        throw new Error('Session ID mismatch');
      }

      // Save using existing utilities
      await this.saveLocalSession(data);
      return data;
    } catch (error) {
      if (error instanceof ApiErrorClass) throw error;
      // Re-throw validation errors
      if (
        error instanceof Error &&
        (error.message === 'Session ID is required' ||
          error.message === 'Session data is required' ||
          error.message === 'Session ID mismatch' ||
          error.message.startsWith('Invalid session data'))
      ) {
        throw error;
      }
      console.error(`Failed to save session progress for ${sessionId}:`, error);
      throw new Error('Unable to save session progress. Please try again.');
    }
  }

  /**
   * Resume an incomplete session
   *
   * Retrieves and validates an incomplete session from local storage.
   *
   * @param sessionId - Session ID to resume
   * @returns Promise resolving to resumed session state
   * @throws Error if session cannot be resumed
   */
  public async resumeSession(sessionId: ID): Promise<SessionState> {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    try {
      const localSession = await this.getLocalSession();
      if (!localSession) {
        throw new Error('No incomplete session found to resume');
      }

      if (localSession.workout.workout_id !== sessionId.toString()) {
        throw new Error(
          'Session ID mismatch. Cannot resume different session.'
        );
      }

      // Validate session data using schema validation
      const parseResult = safeValidateSessionState(localSession);
      if (!parseResult.success) {
        const errorMessages = parseResult.error.issues.map((e) => e.message);
        throw new Error(
          `Cannot resume invalid session: ${errorMessages.join(', ')}`
        );
      }

      // Ensure session is marked as active for resumption
      if (!localSession.active) {
        const resumedSession = mergeSessionProgress(localSession, {
          active: true,
        });
        await this.saveLocalSession(resumedSession);
        return resumedSession;
      }

      return localSession;
    } catch (error) {
      if (error instanceof ApiErrorClass) throw error;
      // Re-throw validation errors
      if (
        error instanceof Error &&
        (error.message === 'Session ID is required' ||
          error.message === 'No incomplete session found to resume' ||
          error.message ===
            'Session ID mismatch. Cannot resume different session.' ||
          error.message.startsWith('Cannot resume invalid session'))
      ) {
        throw error;
      }
      console.error(`Failed to resume session ${sessionId}:`, error);
      throw new Error('Unable to resume session. Please try again.');
    }
  }

  // =============================================================================
  // PRIVATE UTILITY METHODS (using existing session-utils)
  // =============================================================================

  /**
   * Get session data from local storage using existing utilities
   *
   * @returns Session state or null if not found
   * @private
   */
  private async getLocalSession(): Promise<SessionState | null> {
    try {
      const serializedData =
        await this.storageService.get<SerializedSession>(sessionStorageKey());
      if (!serializedData) {
        return null;
      }

      // Use existing utility to deserialize
      return deserializeSessionFromStorage(serializedData);
    } catch (error) {
      console.error('Failed to retrieve session from storage:', error);
      return null;
    }
  }

  /**
   * Save session data to local storage using existing utilities
   *
   * @param session - Session state to save
   * @private
   */
  private async saveLocalSession(session: SessionState): Promise<void> {
    try {
      // Use existing utility to serialize
      const serialized = serializeSessionForStorage(session);
      await this.storageService.set(sessionStorageKey(), serialized);
    } catch (error) {
      console.error('Failed to save session to storage:', error);
      // eslint-disable-next-line do-errors/no-clobber-typed-error -- local storage-write failure (storageService.set), not a coordinator ApiErrorClass; no typed API error flows through this catch
      throw new Error('Unable to persist session data locally');
    }
  }

  /**
   * Clear session data from local storage
   *
   * @private
   */
  private async clearLocalSession(): Promise<void> {
    try {
      await this.storageService.remove(sessionStorageKey());
    } catch (error) {
      console.error('Failed to clear session from storage:', error);
    }
  }

  // =============================================================================
  // PUBLIC UTILITY METHODS
  // =============================================================================

  /**
   * Check if there's an active local session
   *
   * @returns True if an active session exists locally
   */
  public async hasActiveLocalSession(): Promise<boolean> {
    const session = await this.getLocalSession();
    return session !== null && session.active;
  }

  /**
   * Get the current active session if it exists
   *
   * @returns Active session state or null
   */
  public async getCurrentSession(): Promise<SessionState | null> {
    const session = await this.getLocalSession();
    return session?.active ? session : null;
  }

  // ---------------------------------------------------------------------------
  // VALIDATION METHODS
  // ---------------------------------------------------------------------------

  /**
   * Validate workout session data using Zod schema
   * Ensures data meets requirements before API calls
   *
   * @param data - Workout session data to validate
   * @throws Error if validation fails
   */
  protected validateSessionData(data: WorkoutSessionInput): void {
    try {
      validateWorkoutSession(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod(
          "Couldn't save the workout session",
          error
        );
      }
      throw new InvalidDataError(
        "Couldn't save the workout session: the data is in an unexpected format"
      );
    }
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

// Register as singleton and create/get singleton instance
let isRegistered = false;

/**
 * Get the workout session service instance (lazy initialization with DI)
 */
export const getWorkoutSessionService = (): WorkoutSessionService => {
  if (!container.isRegistered(TOKENS.STORAGE)) {
    throw new Error(
      'StorageService is not registered. Call registerStorageService() first.'
    );
  }

  if (!isRegistered) {
    container.registerSingleton(WorkoutSessionService);
    isRegistered = true;
  }
  return container.resolve(WorkoutSessionService);
};
